import React, { useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Progress,
} from 'reactstrap';
import i18n from '@/utils/i18n';
import { useRequestOrderFulfillmentPictureUploadUrlMutation } from '../../../../graphql/mutations/requestOrderFulfillmentPictureUploadUrl.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import uploadToS3 from '@/utils/imageUpload';
import Spinner from '../../../../components/LoadingSpinner';

type UploadOrderFulfillmentPicturesModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onUploadComplete: (pictureCount: number) => void;
  orderFulfillmentId?: string;
};

const UploadOrderFulfillmentPicturesModal = ({
  isOpen,
  setIsOpen,
  onUploadComplete,
  orderFulfillmentId,
}: UploadOrderFulfillmentPicturesModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [comment, setComment] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [requestUploadUrl] =
    useRequestOrderFulfillmentPictureUploadUrlMutation();

  const fileCount = imageFiles.length;

  async function execute() {
    setProcessedCount(0);
    if (fileCount === 0) {
      setIsOpen(false);
      return;
    }

    setIsSubmitting(true);

    let batchSize = 5;
    for (let i = 0; i < fileCount; i += batchSize) {
      await uploadBatch(i, Math.min(i + batchSize, fileCount));
    }

    // wait so s3 has a chance to process the image
    await new Promise((resolve) => setTimeout(resolve, 3500));

    setIsSubmitting(false);
    setIsOpen(false);
    onUploadComplete(fileCount);
  }

  async function uploadBatch(start: number, end: number) {
    const promises = imageFiles.slice(start, end).map(async (file) => {
      const res = await requestUploadUrl({
        variables: {
          file_type: file.type,
          order_fulfillment_id: orderFulfillmentId,
          comment,
        },
      });

      if (res.errors || !res.data?.requestOrderFulfillmentPictureUploadUrl) {
        LayoutStoreInstance.setError(
          i18n.t('OrderFulfillmentPictures:uploadError'),
        );

        console.log(res.errors);
        setIsSubmitting(false);
        setIsOpen(false);
        return;
      }

      const { url } = res.data.requestOrderFulfillmentPictureUploadUrl;

      await uploadToS3(file, url);
    });
    await Promise.all(promises);
    setProcessedCount(end);
  }

  return (
    <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
      <ModalHeader>
        <p>{i18n.t('OrderFulfillmentPictures:uploadHeader')}</p>
      </ModalHeader>

      <ModalBody>
        {isSubmitting ? (
          <div className="text-center">
            {processedCount !== fileCount && (
              <>
                <Progress animated value={processedCount} max={fileCount} />
                <div>
                  {i18n.t('OrderFulfillmentPictures:uploadingPictures', {
                    processedCount,
                    fileCount,
                  })}
                </div>
              </>
            )}
            {processedCount === fileCount && (
              <div className="d-flex justify-content-center">
                <Spinner />
              </div>
            )}
          </div>
        ) : (
          <Form>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('OrderFulfillmentPictures:selectPictures')}
              </Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.avif,.heic,.heif"
                multiple
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setImageFiles(
                    e.target.files ? Array.from(e.target.files) : [],
                  )
                }
              />
            </FormGroup>
            <FormGroup>
              <Label className="me-2">
                {i18n.t('OrderFulfillmentPictures:comment')}
              </Label>
              <Input
                type="textarea"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setComment(e.target.value)
                }
              />
            </FormGroup>
          </Form>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          onClick={execute}
          disabled={imageFiles.length === 0}
        >
          {i18n.t('OrderFulfillmentPictures:upload')}
        </Button>
        <Button
          onClick={() => setIsOpen(false)}
          disabled={isSubmitting}
          color="danger"
        >
          {i18n.t('Common:cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UploadOrderFulfillmentPicturesModal;
